import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { ArrowRightFill, Close as IconClose, QuizIcon } from '@lyearn/icons';
import {
  Box,
  Button,
  Divider,
  DotSeparator,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@lyearn/ui';
import CountdownTimer from '@/modules/corporate/modules/quiz/views/QuizDetails/components/QuizDetailsQuizPlayer/components/QuizPlayerHeader/components/QuestionHeader/components/CountdownTimer';
import { QuizPlayerActionsEnum } from '@/modules/corporate/modules/quiz/views/QuizPlayer/hooks/useQuizPlayer/actions';
import {
  View,
  ViewTypeEnum,
} from '@/modules/corporate/modules/quiz/views/QuizPlayer/hooks/useQuizPlayer/types';
import { RenderQuizPlayerHeaderParams } from '@/modules/corporate/modules/quiz/views/QuizPlayer/types';
import { TimeLimitApplicableOnEnum } from '@/types/schema';

import SubmitQuizButton from '../../../../../quiz/views/QuizPlayer/components/SubmitQuizButton';
import { getIsQuizSidebarEnabled } from '../../../../../quiz/views/QuizPlayer/hooks/useQuizPlayerTimer/utils';
import { useCourseQuizNextLesson } from '../../hooks/useCourseQuizNextLesson';

const emptyNumberFunc = () => 0;

function CourseQuizPlayerHeader({
  content,
  view,
  timeLeft,
  onClose: onCloseProp,
  onSubmitQuiz,
  getUnansweredQuestions,
  getUnsubmittedQuestionsCount,
  onCloseQuiz,
  onAction,
}: Omit<RenderQuizPlayerHeaderParams, 'view'> & { view?: View; onClose?: () => void }) {
  const { t } = useTranslation(['QuizPlayer']);
  const count = content.quizQuestionsConfig.questionCount;
  const { isMobile } = useScreen();
  const isQuestionView = view?.type === ViewTypeEnum.Question;
  const { onNextLessonClick, isNextLessonAvailable } = useCourseQuizNextLesson();

  const onClose = React.useCallback(() => {
    if (isQuestionView) {
      onCloseQuiz?.();
    }

    onCloseProp?.();
  }, [isQuestionView, onCloseQuiz, onCloseProp]);

  const renderCountDownTimer = React.useCallback(() => {
    if (
      !isQuestionView ||
      content.settings?.timeLimitApplicableOn !== TimeLimitApplicableOnEnum.Quiz
    ) {
      return null;
    }

    if (isMobile)
      return (
        <Box className="flex-shrink-0 px-8 py-4">
          <CountdownTimer timeLeft={timeLeft} />
        </Box>
      );

    return (
      <Tooltip placement="bottom" title={t('QuizPlayer:TimeLeftOnQuiz')!}>
        <CountdownTimer className="absolute start-1/2" timeLeft={timeLeft} />
      </Tooltip>
    );
  }, [isQuestionView, content.settings?.timeLimitApplicableOn, isMobile, timeLeft, t]);

  const onSubmitQuizAction = React.useCallback(() => {
    if (!getIsQuizSidebarEnabled(content.settings) && view?.type === ViewTypeEnum.Question) {
      onAction?.({
        type: QuizPlayerActionsEnum.SubmitQuestionAndSubmitQuiz,
        payload: { index: view.index },
      } as any);
    } else {
      onSubmitQuiz?.();
    }
  }, [content.settings, onAction, onSubmitQuiz, view]);

  return (
    <Stack
      alignItems="center"
      className="h-[7.6rem] xs:h-[9rem] border-b border-border pe-12 xs:pe-20 ps-24 xs:ps-16"
      justifyContent="between">
      <Stack alignItems="center" className="py-16 xs:py-20">
        <QuizIcon height={44} width={44} />
        <Stack className="flex-shrink ms-16 xs:flex-1" direction="col">
          <Typography
            className="flex-shrink"
            color="text-text-primary"
            numberOfLines={isMobile ? 2 : undefined}
            variant={isMobile ? 'heading-s' : 'label-01'}>
            {content.name}
          </Typography>
          {!isQuestionView ? null : (
            <Typography
              className="mt-2 xs:mt-0"
              color="text-text-secondary"
              variant={isMobile ? 'body-short-02' : 'body-short-03'}>
              <DotSeparator gapX={4}>
                {t('Common:ResourceType.Quiz')}
                {isMobile
                  ? t('QuizPlayer:questions', {
                      count,
                    })
                  : null}
              </DotSeparator>
            </Typography>
          )}
        </Stack>
      </Stack>
      {renderCountDownTimer()}
      {isMobile ? null : (
        <Stack alignItems="center" gapX={16}>
          {onSubmitQuiz && isQuestionView && !isMobile ? (
            <>
              <SubmitQuizButton
                getUnansweredQuestions={getUnansweredQuestions ?? emptyNumberFunc}
                getUnsubmittedQuestionsCount={getUnsubmittedQuestionsCount ?? emptyNumberFunc}
                showConfirmation={getIsQuizSidebarEnabled(content.settings)}
                onSubmitQuiz={onSubmitQuizAction}
              />
              <Divider className="h-32" orientation="vertical" />
            </>
          ) : null}
          {isNextLessonAvailable && view?.type === ViewTypeEnum.Review ? (
            <>
              <Button
                EndIcon={ArrowRightFill}
                appearance="secondary"
                className="xs:flex xs:flex-1 !heading-xxs"
                shape="pill"
                size="medium"
                variant="filled"
                onClick={onNextLessonClick}>
                {t('Course:Lesson.NextLesson')}
              </Button>
              <Divider className="bg-divider h-auto" orientation="vertical" size={2} />
            </>
          ) : null}
          <Tooltip placement="bottom" title={t('Course:GoBackToCurrentLesson')}>
            <IconButton appearance="secondary" size="xl" variant="ghost" onClick={onClose}>
              <IconClose className="!h-26 !w-26" />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
}

export default CourseQuizPlayerHeader;
